import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogSelect } from "@/components/ui/dialog-select";
import { Product } from "@/types/product";
import { Plus } from "lucide-react";
import { DialogDatePicker } from "@/components/ui/dialog-date-picker";

interface AddProductDialogProps {
  customerId: string;
  onAdd: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  trigger?: React.ReactNode;
}

export function AddProductDialog({ customerId, onAdd, trigger }: AddProductDialogProps) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    deviceName: "",
    deviceDescription: "",
    salePrice: "",
    monthlyInstallment: "",
    currency: "" as "" | "IQD" | "USD",
    date: new Date(),
    notes: "",
  });

  const currencyOptions = [
    { value: "IQD", label: "دینار (IQD)" },
    { value: "USD", label: "دۆلار (USD)" },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      customerId,
      deviceName: formData.deviceName,
      deviceDescription: formData.deviceDescription,
      salePrice: parseFloat(formData.salePrice),
      monthlyInstallment: parseFloat(formData.monthlyInstallment),
      installments: 0,
      currency: formData.currency as "IQD" | "USD",
      paidAmount: 0,
      date: formData.date.toISOString().split('T')[0],
      notes: formData.notes,
    });
    setFormData({
      deviceName: "",
      deviceDescription: "",
      salePrice: "",
      monthlyInstallment: "",
      currency: "",
      date: new Date(),
      notes: "",
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            زیادکردنی کاڵا
            <Plus className="h-4 w-4 mr-2" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle className="text-right">زیادکردنی کاڵای نوێ</DialogTitle>
          <DialogDescription className="text-right">زانیاریەکانی کاڵا پڕ بکەوە</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deviceName">ناوی ئامێر *</Label>
              <Input
                id="deviceName"
                value={formData.deviceName}
                onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                required
                placeholder="ناوی ئامێر"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deviceDescription">وەسفی ئامێر</Label>
              <Input
                id="deviceDescription"
                value={formData.deviceDescription}
                onChange={(e) => setFormData({ ...formData, deviceDescription: e.target.value })}
                placeholder="وەسفی ئامێر"
              />
            </div>
          </div>

          {/* دراو */}
          <div className="space-y-2">
            <Label>دراو *</Label>
            <DialogSelect
              value={formData.currency}
              onChange={(value) => setFormData({ ...formData, currency: value as "IQD" | "USD" })}
              options={currencyOptions}
              placeholder="هەڵبژێرە"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="salePrice">نرخی فرۆشتن *</Label>
              <Input
                id="salePrice"
                type="number"
                value={formData.salePrice}
                onChange={(e) => setFormData({ ...formData, salePrice: e.target.value })}
                required
                placeholder="0"
                dir="ltr"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthlyInstallment">قیستی مانگانە *</Label>
              <Input
                id="monthlyInstallment"
                type="number"
                value={formData.monthlyInstallment}
                onChange={(e) => setFormData({ ...formData, monthlyInstallment: e.target.value })}
                required
                placeholder="0"
                dir="ltr"
              />
            </div>
          </div>

          <div className="space-y-2">
            <DialogDatePicker
              value={formData.date}
              onChange={(date) => setFormData({ ...formData, date })}
              label="بەرواری کڕین *"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">تێبینی</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="تێبینی..."
              rows={2}
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              پاشگەزبوونەوە
            </Button>
            <Button type="submit">زیادکردن</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
